import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { getMcpStatus, testMcpCredential, McpStatusEntry, McpId } from '../api';
import { MCP_REGISTRY, claudeCodeSnippet, claudeAiSteps, McpRegistryEntry } from '../mcpRegistry';

// ---------------------------------------------------------------------------
// Authenticated Dashboard — shown when the user is logged in
// ---------------------------------------------------------------------------

const AuthenticatedDashboard: React.FC = () => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState<McpStatusEntry[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [expanded, setExpanded] = useState<Set<McpId>>(new Set());
  const [testing, setTesting] = useState<McpId | null>(null);
  const [testResults, setTestResults] = useState<Partial<Record<McpId, { ok: boolean; detail: string }>>>({});
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    getMcpStatus()
      .then(setStatuses)
      .catch(err => setLoadError(err instanceof Error ? err.message : 'Failed to load MCP status.'));
  }, []);

  const copy = async (field: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const toggleExpand = (id: McpId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runTest = async (id: McpId) => {
    setTesting(id);
    try {
      const result = await testMcpCredential(id);
      setTestResults(prev => ({ ...prev, [id]: result }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [id]: { ok: false, detail: err instanceof Error ? err.message : 'Test failed.' } }));
    } finally {
      setTesting(null);
    }
  };

  const renderCard = (entry: McpRegistryEntry) => {
    const status = statuses?.find(s => s.id === entry.id);
    const isOpen = expanded.has(entry.id);
    const test = testResults[entry.id];
    const credState = test
      ? (test.ok ? 'configured-working' : status?.credential.state ?? 'not-configured')
      : status?.credential.state;
    const credDetail = test ? test.detail : status?.credential.detail;
    const testable = entry.credentialKind !== 'none' && status?.credential.state !== 'not-configured';

    return (
      <div key={entry.id} style={dash.card}>
        <div style={dash.cardHeader}>
          <div>
            <h2 style={dash.cardTitle}>{entry.name}</h2>
            <p style={dash.cardBlurb}>{entry.blurb}</p>
          </div>
          {status && (
            <span style={status.healthy ? dash.healthUp : dash.healthDown}>
              {status.healthy ? '● Active' : '● Unreachable'}
            </span>
          )}
        </div>

        <div style={dash.statusRows}>
          <div style={dash.statusRow}>
            <span style={dash.statusLabel}>Access</span>
            {status ? (
              <span style={status.access.allowed ? dash.accessAllowed : dash.accessDenied}>
                {status.access.allowed ? 'Allowed' : 'Denied'}
                {status.access.hasOverride ? ' (per-user override)' : ''}
              </span>
            ) : <span style={dash.muted}>…</span>}
          </div>
          <div style={dash.statusRow}>
            <span style={dash.statusLabel}>Credentials</span>
            {status ? (
              <span style={dash.credCell}>
                <span style={
                  credState === 'configured-working' ? dash.credWorking
                    : credState === 'configured-untested' ? dash.credUntested
                    : credState === 'n/a' ? dash.muted : dash.credMissing
                }>
                  {credState === 'configured-working' ? 'Working'
                    : credState === 'configured-untested' ? 'Configured'
                    : credState === 'n/a' ? 'Not needed' : 'Not configured'}
                </span>
                <span style={dash.credDetail}>{credDetail}</span>
                {testable && (
                  <button style={dash.testBtn} disabled={testing === entry.id} onClick={() => runTest(entry.id)}>
                    {testing === entry.id ? 'Testing…' : 'Test'}
                  </button>
                )}
                {status.credential.state === 'not-configured' && entry.credentialKind !== 'none' && (
                  <Link to="/credentials" style={dash.setupLink}>Set up →</Link>
                )}
              </span>
            ) : <span style={dash.muted}>…</span>}
          </div>
        </div>

        <button style={dash.connectToggle} aria-expanded={isOpen} onClick={() => toggleExpand(entry.id)}>
          {isOpen ? '▾' : '▸'} Connect
        </button>

        {isOpen && (
          <div style={dash.connectSection}>
            <div style={dash.endpointRow}>
              <code style={dash.endpointCode}>{entry.endpoint}</code>
              <button style={dash.copyBtn} onClick={() => copy(`${entry.id}-url`, entry.endpoint)}>
                {copiedField === `${entry.id}-url` ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <h3 style={dash.connectHeading}>Claude Code (CLI)</h3>
            <div style={dash.endpointRow}>
              <code style={dash.endpointCode}>{claudeCodeSnippet(entry)}</code>
              <button style={dash.copyBtn} onClick={() => copy(`${entry.id}-cli`, claudeCodeSnippet(entry))}>
                {copiedField === `${entry.id}-cli` ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <h3 style={dash.connectHeading}>Claude.ai (Web)</h3>
            <ol style={dash.stepsList}>
              {claudeAiSteps(entry).map((step, i) => <li key={i} style={dash.step}>{step}</li>)}
            </ol>
            <p style={dash.approveTip}>
              <strong>Tip:</strong> the first time a tool runs, approve it once — choose
              {' '}<em>“Allow always”</em> on claude.ai, or add{' '}
              <code style={dash.inlineCode}>mcp__{entry.id}__*</code> to{' '}
              <code style={dash.inlineCode}>permissions.allow</code> in Claude Code — so calls
              aren’t blocked client-side as “MCP tool call requires approval”. This is a Claude
              approval gate, not a DI access check.
            </p>
            <p style={dash.hint}>{entry.credentialHint}.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={dash.container}>
      <h1 style={dash.heading}>Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
      <p style={dash.subheading}>Your DI MCP fleet — server status, your access, and how to connect.</p>
      {loadError && <div style={dash.errorBanner}>{loadError}</div>}
      <div style={dash.cardsGrid}>
        {MCP_REGISTRY.map(renderCard)}
      </div>
      <div style={dash.quickLinks}>
        <Link to="/credentials" style={dash.quickLink}>Credentials</Link>
        <Link to="/my-requests" style={dash.quickLink}>My Requests</Link>
        <Link to="/request" style={dash.quickLink}>New Request</Link>
      </div>
    </div>
  );
};

const dash: Record<string, React.CSSProperties> = {
  container: { maxWidth: 920, margin: '0 auto', padding: '2rem 1rem' },
  heading: { fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.25rem' },
  subheading: { color: '#888', fontSize: '0.9rem', marginBottom: '1.5rem' },
  errorBanner: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 6, padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '1rem' },
  card: { background: '#fff', borderRadius: 8, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', gap: '0.5rem' },
  cardTitle: { fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.15rem' },
  cardBlurb: { fontSize: '0.8rem', color: '#888' },
  healthUp: { color: '#28a745', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' as const },
  healthDown: { color: '#dc3545', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' as const },
  statusRows: { display: 'flex', flexDirection: 'column' as const, gap: '0.4rem', marginBottom: '0.75rem' },
  statusRow: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  statusLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase' as const, letterSpacing: '0.04em', width: 90, flexShrink: 0 },
  accessAllowed: { color: '#155724', background: '#d4edda', padding: '0.1rem 0.5rem', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600 },
  accessDenied: { color: '#721c24', background: '#f8d7da', padding: '0.1rem 0.5rem', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600 },
  credCell: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' as const },
  credWorking: { color: '#155724', background: '#d4edda', padding: '0.1rem 0.5rem', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600 },
  credUntested: { color: '#0c5460', background: '#d1ecf1', padding: '0.1rem 0.5rem', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600 },
  credMissing: { color: '#856404', background: '#fff3cd', padding: '0.1rem 0.5rem', borderRadius: 10, fontSize: '0.78rem', fontWeight: 600 },
  credDetail: { fontSize: '0.75rem', color: '#999' },
  muted: { fontSize: '0.78rem', color: '#aaa' },
  testBtn: { background: 'transparent', border: '1px solid #4a6cf7', color: '#4a6cf7', padding: '0.1rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' },
  setupLink: { fontSize: '0.78rem', color: '#4a6cf7', fontWeight: 600, textDecoration: 'none' },
  connectToggle: { background: 'none', border: 'none', color: '#4a6cf7', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, padding: '0.25rem 0' },
  connectSection: { marginTop: '0.5rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.75rem' },
  connectHeading: { fontSize: '0.8rem', fontWeight: 700, color: '#555', margin: '0.75rem 0 0.35rem' },
  endpointRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  endpointCode: { flex: 1, fontFamily: 'monospace', fontSize: '0.75rem', background: '#f8f9fa', borderRadius: 4, padding: '0.4rem 0.6rem', overflowX: 'auto' as const, whiteSpace: 'nowrap' as const },
  copyBtn: { background: '#4a6cf7', color: '#fff', border: 'none', padding: '0.3rem 0.7rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  stepsList: { paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  step: { fontSize: '0.82rem', color: '#444' },
  approveTip: { fontSize: '0.78rem', color: '#555', background: '#f1f6ff', border: '1px solid #d6e4ff', borderRadius: 6, padding: '0.5rem 0.65rem', marginTop: '0.6rem', lineHeight: 1.5 },
  inlineCode: { fontFamily: 'monospace', fontSize: '0.72rem', background: '#e8eefc', borderRadius: 3, padding: '0.05rem 0.3rem' },
  hint: { fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' },
  quickLinks: { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
  quickLink: { color: '#4a6cf7', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' },
};

// ---------------------------------------------------------------------------
// Landing page — unauthenticated hero (unchanged)
// ---------------------------------------------------------------------------

const Landing: React.FC = () => {
  const { loading, user, login } = useAuth();

  if (loading) {
    return (
      <div style={styles.center}>
        <p>Loading...</p>
      </div>
    );
  }

  if (user) {
    return <AuthenticatedDashboard />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.hero}>
        <h1 style={styles.title}>MCP Access Request Portal</h1>
        <p style={styles.subtitle}>
          Request access to the Atlassian MCP (Model Context Protocol) server.
          Sign in with Google or your email and password to submit an access
          request, track its status, and set up MFA once approved.
        </p>

        <div style={styles.features}>
          <div style={styles.feature}>
            <div style={styles.featureIcon}>1</div>
            <h3 style={styles.featureTitle}>Sign In</h3>
            <p style={styles.featureText}>
              Authenticate with Google or your email and password via Cognito.
            </p>
          </div>
          <div style={styles.feature}>
            <div style={styles.featureIcon}>2</div>
            <h3 style={styles.featureTitle}>Request Access</h3>
            <p style={styles.featureText}>
              Submit a request specifying the access scope you need.
            </p>
          </div>
          <div style={styles.feature}>
            <div style={styles.featureIcon}>3</div>
            <h3 style={styles.featureTitle}>Get Approved</h3>
            <p style={styles.featureText}>
              An admin will review and approve or deny your request.
            </p>
          </div>
          <div style={styles.feature}>
            <div style={styles.featureIcon}>4</div>
            <h3 style={styles.featureTitle}>Set Up MFA</h3>
            <p style={styles.featureText}>
              Configure TOTP-based MFA to secure your MCP access.
            </p>
          </div>
        </div>

        <button onClick={login} style={styles.signInBtn}>
          Sign in
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  hero: {
    textAlign: 'center' as const,
    maxWidth: 720,
    padding: '3rem 1rem',
  },
  title: {
    fontSize: '2.2rem',
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: '1rem',
  },
  subtitle: {
    fontSize: '1.05rem',
    color: '#555',
    lineHeight: 1.7,
    marginBottom: '2.5rem',
  },
  features: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2.5rem',
  },
  feature: {
    background: '#fff',
    borderRadius: 8,
    padding: '1.5rem 1rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  featureIcon: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#4a6cf7',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '1rem',
    marginBottom: '0.75rem',
  },
  featureTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    marginBottom: '0.4rem',
  },
  featureText: {
    fontSize: '0.8rem',
    color: '#777',
    lineHeight: 1.5,
  },
  signInBtn: {
    background: '#4a6cf7',
    color: '#fff',
    border: 'none',
    padding: '0.85rem 2.5rem',
    borderRadius: 6,
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(74,108,247,0.3)',
    transition: 'background 0.2s',
  },
};

export default Landing;
